import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import useUserContext from './useUserContext';
import { GameErrorPayload, GameInstance, GameState, GameUpdatePayload } from '../types/types';
import { getGameById, joinGame, leaveGame } from '../services/gamesService';

/**
 * Custom hook to manage the state and logic for the game page, including joining, leaving the game, and handling game updates.
 * @returns An object containing the following:
 * - `gameInstance`: The current game instance, or null if no game is joined.
 * - `error`: A string containing any error messages related to the game, or null if no errors exist.
 * - `handleLeaveGame`: A function to leave the current game and navigate back to the game list.
 */
const useGamePage = () => {
  const { user, socket } = useUserContext();
  const { gameID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [gameInstance, setGameInstance] = useState<GameInstance<GameState> | null>(null);
  const [joinedGameID, setJoinedGameID] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Check if joining via invitation link
  const queryParams = new URLSearchParams(location.search);
  const invitationCode = queryParams.get('code');
  const isInviteLink = location.pathname.includes('/games/join/') && invitationCode;

  const handleLeaveGame = async () => {
    if (joinedGameID && gameInstance?.state.status !== 'OVER') {
      await leaveGame(joinedGameID, user.username);
      setGameInstance(null);
      setJoinedGameID('');
    }

    socket.emit('leaveGame', joinedGameID);
    navigate('/games');
  };

  const fetchGameDetails = useCallback(async (id: string) => {
    try {
      const gameInfo = await getGameById(id);
      setGameInstance(gameInfo);
      return gameInfo;
    } catch (err) {
      setError(`Error loading game: ${err}`);
      return null;
    }
  }, []);

  const handleJoinGame = useCallback(
    async (id: string) => {
      try {
        const gameInfo = await fetchGameDetails(id);

        if (gameInfo?.state.status === 'OVER') {
          setJoinedGameID(gameInfo.gameID);
          socket.emit('joinGame', gameInfo.gameID);
          return;
        }

        const joinedGame = await joinGame(id, user.username);
        setGameInstance(joinedGame);
        setJoinedGameID(joinedGame.gameID);
        socket.emit('joinGame', joinedGame.gameID);

        if (isInviteLink) {
          navigate(`/games/${id}`, { replace: true });
        }
      } catch (err) {
        // Try to fetch the game - it might be over but still viewable
        try {
          const gameInfo = await fetchGameDetails(id);
          if (gameInfo?.state.status === 'OVER') {
            setError('This game has already ended. You can view the results but cannot join.');
          } else {
            setError('Failed to join the game. It may no longer exist.');
          }
        } catch (fetchError) {
          setError('Error loading game');
        }
      }
    },
    [user.username, socket, fetchGameDetails, isInviteLink, navigate],
  );

  useEffect(() => {
    if (gameID) {
      handleJoinGame(gameID);
    }

    const handleGameUpdate = (updatedState: GameUpdatePayload) => {
      setGameInstance(prevGameInstance =>
        prevGameInstance ? updatedState.gameInstance : prevGameInstance,
      );
      setError(null);
    };

    const handleGameError = (gameError: GameErrorPayload) => {
      if (gameError.player === user.username) {
        setError(gameError.error);
      }
    };

    socket.on('gameUpdate', handleGameUpdate);
    socket.on('gameError', handleGameError);

    return () => {
      socket.off('gameUpdate', handleGameUpdate);
      socket.off('gameError', handleGameError);
    };
  }, [gameID, socket, user.username, handleJoinGame]);

  return {
    gameInstance,
    error,
    handleLeaveGame,
  };
};

export default useGamePage;
